import React, { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PageHeader, EditorialButton } from "@/lib/editorial";
import { docsApi } from "@/lib/api";
import { Upload, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATEGORIES = [
  { value: "general", label: "General" },
  { value: "portfolio", label: "Portfolio / Case Studies" },
  { value: "offers", label: "Offer Details" },
  { value: "playbooks", label: "Outreach Playbooks" },
  { value: "other", label: "Other" },
];

export default function KnowledgeBase() {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [category, setCategory] = useState("general");
  const [uploading, setUploading] = useState(false);

  const { data: docs = [], isLoading } = useQuery({
    queryKey: ["documents"],
    queryFn: docsApi.list,
  });

  const deleteMut = useMutation({
    mutationFn: (id) => docsApi.remove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["documents"] }); toast("Document removed."); },
    onError: (e) => toast.error(e.message || "Delete failed."),
  });

  async function handleFiles(files) {
    if (!files?.length) return;
    setUploading(true);
    let ok = 0, fail = 0;
    for (const f of Array.from(files)) {
      try {
        await docsApi.upload(f, category);
        ok++;
      } catch (e) {
        toast.error(`${f.name}: ${e.message}`);
        fail++;
      }
    }
    setUploading(false);
    if (ok) { toast(`${ok} document${ok > 1 ? "s" : ""} uploaded.`); qc.invalidateQueries({ queryKey: ["documents"] }); }
  }

  function onDrop(e) {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  }

  // Group by whatever categories actually exist in the data, not just the
  // ones in the upload dropdown — a document's category is free-form on
  // the backend (e.g. "capabilities" for KB docs seeded outside the
  // upload form), so filtering strictly against CATEGORIES silently
  // dropped any document whose category wasn't in that fixed list.
  const knownLabels = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));
  const categoriesPresent = [...new Set(docs.map((d) => d.category || "general"))];
  const grouped = categoriesPresent
    .map((value) => ({
      value,
      label: knownLabels[value] || value.replace(/[_-]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      items: docs.filter((d) => (d.category || "general") === value),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <div className="px-8 lg:px-14 py-12 max-w-[1200px]">
      <PageHeader
        eyebrow="Company"
        tape="Knowledge"
        rest="Base"
        sub="Upload company documents, case studies, and offer details. The AI uses these to generate specific, evidence-backed suggested replies in the Inbox."
        testId="kb-title"
      />

      {/* Upload area */}
      <div
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
        className="border-2 border-dashed border-[var(--rule-light)] bg-[var(--paper-2)] p-10 text-center mb-8 transition-colors hover:border-[var(--blue)]"
      >
        <Upload className="w-8 h-8 mx-auto text-[var(--muted-light)] mb-3" />
        <div className="font-display text-[20px] mb-1">Drop files here</div>
        <div className="text-[13px] text-[var(--muted-light)] mb-4">
          .docx, .txt, or .md files. Text is extracted automatically.
        </div>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-52 rounded-none border-[var(--rule-light)] focus:ring-0 h-9 text-[12px] uppercase tracking-[0.14em]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <EditorialButton onClick={() => fileRef.current?.click()} disabled={uploading}>
            {uploading ? "Uploading…" : "Choose files"}
          </EditorialButton>
        </div>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept=".docx,.txt,.md"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>

      {/* Document list */}
      {isLoading && (
        <div className="text-center py-12 text-[var(--muted-light)]">Loading…</div>
      )}
      {!isLoading && docs.length === 0 && (
        <div className="text-center py-12 text-[var(--muted-light)] italic font-display text-xl">
          No documents yet. Upload some to get started.
        </div>
      )}

      {grouped.map((group) => (
        <div key={group.value} className="mb-8">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--muted-light)] mb-3">
            {group.label}
          </div>
          <div className="border border-[var(--rule-light)] bg-white divide-y divide-[var(--rule-light)]">
            {group.items.map((doc) => (
              <div key={doc.id} className="flex items-center gap-4 px-5 py-4">
                <FileText className="w-4 h-4 text-[var(--blue)] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-display text-[15px] truncate">{doc.name}</div>
                  <div className="text-[11px] font-mono text-[var(--muted-light)] mt-0.5">
                    {doc.file_name} &middot; {(doc.char_count / 1000).toFixed(1)}k chars &middot; {doc.uploaded_at?.slice(0, 10)}
                  </div>
                </div>
                <button
                  onClick={() => deleteMut.mutate(doc.id)}
                  disabled={deleteMut.isPending}
                  className="text-[var(--muted-light)] hover:text-rose-500 transition-colors p-1"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
